import { xcss } from '@atlaskit/primitives';
import type { XCSS } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useScrollFade } from '@databases/action-bar/useScrollFade';
import { getVersionId, useDatabaseContent } from '@databases/database-state';
import { isEmbed, isMobile } from '@databases/modes';
import { type PanelsType } from '@databases/panel/PanelContext';
import usePanelState from '@databases/panel/usePanelState';

const baseHorizontalFadeScrollStyles = {
  //Removing the gradient for now because of
  //https://product-fabric.atlassian.net/browse/CTD-5359
  display: 'none',
  position: 'absolute' as const,
  height: '100%',
  width: '55px',
  background: `linear-gradient(90deg, rgba(255, 255, 255, 0.00) 0%, ${token(
    'elevation.surface'
  )} 91.08%)`,
  pointerEvents: 'none' as const,
};

const horizontalFadeScrollStyles: XCSS = xcss({
  ...baseHorizontalFadeScrollStyles,
  right: 'space.0',
  top: '50px',
  // This is the culprit in the issue linked above
  // height: '100vh',
});

// Mobile overrides
const horizontalFadeScrollStylesMobile: XCSS = xcss({
  ...baseHorizontalFadeScrollStyles,
  right: token('space.100', '8px'),
  top: '110px',
});

// Embed overrides
const horizontalFadeScrollStylesEmbed: XCSS = xcss({
  ...baseHorizontalFadeScrollStyles,
  right: '10px',
  top: '214px',
});

const getHorizontalFadeScrollStyles = (): unknown => {
  if (isEmbed()) {
    return horizontalFadeScrollStylesEmbed;
  }
  if (isMobile()) {
    return horizontalFadeScrollStylesMobile;
  }
  return horizontalFadeScrollStyles;
};

export const useFadeGradient = ({
  isConfluencePanelOpen,
}: {
  isConfluencePanelOpen: boolean;
}): { enableFadeGradient: boolean; getHorizontalFadeScrollStyles: () => unknown } => {
  const { isPanelVisible } = usePanelState();
  const panels: Array<keyof PanelsType> = [
    'FieldSettingsPanel',
    'ViewSettingsPanel',
    'EntryViewPanel',
    'DatabaseSetupPanel',
  ];
  const isAnyPanelOpen = panels.some((panel) => isPanelVisible(panel));
  const { referencing } = useDatabaseContent();
  const versionId = getVersionId();
  const showRightFadeGradient = useScrollFade('scrollable-inner-container');

  const enableFadeGradient =
    showRightFadeGradient &&
    !isAnyPanelOpen &&
    !referencing &&
    !isConfluencePanelOpen &&
    !versionId;

  return {
    enableFadeGradient,
    getHorizontalFadeScrollStyles,
  };
};
